import { type ReactElement, useCallback, useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"

import { HeroHeader } from "@/components/ui/hero-section-1"
import { AnimatedGroup } from "@/components/ui/animated-group"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { cn } from "@/lib/utils"
import { Seo } from "@/components/Seo"
import {
  Bolt,
  BookOpen,
  ChevronRight,
  Compass,
  Layers,
  MapPin,
  Rocket,
  Sparkles,
  TrendingUp,
  UtensilsCrossed,
  Truck,
  type LucideIcon,
} from "lucide-react"

type BlogPost = {
  id: string
  slug: string
  title: string
  category: string
  audience: string
  readingTime: string
  summary: string
  primaryKeyword: string
  secondaryKeywords: string[]
  featureHighlights: string[]
  heroImageUrl?: string
}

const fallbackPosts: BlogPost[] = [
  {
    id: "local-restaurant-seo-playbook",
    slug: "local-restaurant-seo-playbook",
    title: "Local Restaurant SEO Playbook: Own “Near Me” Searches in Your City",
    category: "Local SEO",
    audience: "Independent restaurants, small chains, neighborhood cafés",
    readingTime: "7 min read",
    summary:
      "Step‑by‑step restaurant SEO for operators who want to show up when guests search “best restaurant near me”, “late‑night food near me”, or “family‑friendly restaurant in [city]”. We break down Google Business Profile, reviews, local landing pages, and menu optimization.",
    primaryKeyword: "local restaurant SEO",
    secondaryKeywords: [
      "restaurant SEO",
      "SEO for small restaurants",
      "restaurant marketing ideas",
      "Google Business Profile for restaurants",
    ],
    featureHighlights: [
      "Use Braise table ordering and guest messaging to collect more 5‑star reviews.",
      "Sync menu changes once in Braise so your online menu always matches what guests see.",
    ],
  },
  {
    id: "food-truck-pos-guide",
    slug: "food-truck-pos-guide",
    title: "The Food Truck POS Guide: Keep Lines Moving at Festivals and Street Corners",
    category: "Food Trucks",
    audience: "Food trucks, trailers, pop‑ups, and outdoor events",
    readingTime: "8 min read",
    summary:
      "A deep dive into choosing the best POS system for food trucks so you can move the line, keep tickets organized, and track every shift. We cover mobile POS for food trucks, offline payments, tip flows, and how to run multiple trucks from one dashboard.",
    primaryKeyword: "food truck POS system",
    secondaryKeywords: [
      "POS for food trucks",
      "mobile POS for food trucks",
      "food truck point of sale",
      "food truck ordering app",
    ],
    featureHighlights: [
      "Run Food Truck Mode in Braise for rush‑proof handheld and QR ordering.",
      "Use Rush Mode and Autopilot to throttle promos when lines spike at events.",
    ],
  },
  {
    id: "qr-ordering-table-turns",
    slug: "qr-ordering-table-turns",
    title: "How QR Code Ordering Helps Small Restaurants Turn More Tables",
    category: "Guest Experience",
    audience: "Full‑service restaurants, cafés, bars, and food halls",
    readingTime: "6 min read",
    summary:
      "Learn how QR code ordering for restaurants speeds up ordering and payments, reduces wait times, and quietly unlocks higher check averages. We walk through real‑world playbooks for contactless ordering, bar tabs, and pay‑at‑table in tight dining rooms.",
    primaryKeyword: "QR code ordering for restaurants",
    secondaryKeywords: [
      "contactless ordering",
      "restaurant QR ordering",
      "pay at table POS",
      "online ordering for restaurants",
    ],
    featureHighlights: [
      "Launch QR ordering and pay‑at‑table with Braise without rewriting your menu.",
      "Connect QR orders to your existing kitchen display and expo flows.",
    ],
  },
  {
    id: "restaurant-inventory-checklist",
    slug: "restaurant-inventory-checklist",
    title: "Restaurant Inventory Checklist: Cut Food Waste Without Slowing Service",
    category: "Operations",
    audience: "Kitchen managers, operators, and multi‑unit leaders",
    readingTime: "9 min read",
    summary:
      "A practical inventory management checklist for restaurants that want tighter food cost control without drowning crews in spreadsheets. Covers prep lists, count cycles, par levels, and how to plug POS data into your ordering routine.",
    primaryKeyword: "restaurant inventory checklist",
    secondaryKeywords: [
      "restaurant inventory management",
      "reduce food waste in restaurants",
      "food cost control",
      "restaurant stock management",
    ],
    featureHighlights: [
      "Use Braise inventory and retail tools to keep stock, sales, and recipes in one place.",
      "Get real-time alerts when key items are at risk of running out mid-service.",
    ],
  },
  {
    id: "ai-staff-copilot-shift",
    slug: "ai-staff-copilot-shift",
    title: "AI Staff Copilot: Turn Every Shift Into a Guided Service",
    category: "Automation",
    audience: "Multi-unit restaurants, food halls, franchise enablement teams",
    readingTime: "6 min read",
    summary:
      "How Braise AI Staff Copilot listens to throughput, guest sentiment, and prep signals to coach crews in real time. See how prompts, checklists, and proactive nudges keep nightly service on script without 1:1 manager oversight.",
    primaryKeyword: "AI restaurant assistant",
    secondaryKeywords: [
      "restaurant AI copilot",
      "ai coaching for restaurants",
      "hospitality automation",
      "service consistency AI",
    ],
    featureHighlights: [
      "AI Staff Copilot monitors KDS load, guest feedback, and lockers to trigger the right playbook at the right time.",
      "Contextual prompts appear inside Braise handhelds so teams course-correct without leaving the screen they already use.",
    ],
  },
  {
    id: "autopilot-marketing-playbook",
    slug: "autopilot-marketing-playbook",
    title: "Autopilot Marketing: Always-on Campaigns for Restaurants & Food Trucks",
    category: "Growth",
    audience: "Marketing leads at multi-location restaurants, ghost kitchens, food truck fleets",
    readingTime: "7 min read",
    summary:
      "A practical framework for data-backed marketing using Braise Autopilot. Learn how automated SMS, email, and paid boosts react to par breaches, rushes, and guest sentiment without burning your team’s time.",
    primaryKeyword: "restaurant marketing automation",
    secondaryKeywords: [
      "restaurant autopilot",
      "food truck marketing ideas",
      "hospitality lifecycle marketing",
      "restaurant sms automation",
    ],
    featureHighlights: [
      "Autopilot reads live par + POS data to trigger win-back, weather, and rush-based campaigns.",
      "Budget recommender keeps paid boosts within guardrails and logs every move for finance.",
    ],
  },
  {
    id: "rush-mode-operations",
    slug: "rush-mode-operations",
    title: "Rush Mode: Blueprint for Handling Spikes Without Meltdowns",
    category: "Operations",
    audience: "High-volume kitchens, stadium vendors, quick-service restaurants",
    readingTime: "8 min read",
    summary:
      "See how Braise Rush Mode detects line swell, re-routes prep, throttles promos, and keeps handhelds + QR rails balanced. A checklist for festivals, game days, and sudden crowd swings.",
    primaryKeyword: "restaurant rush mode",
    secondaryKeywords: [
      "reduce wait times restaurant",
      "rush management system",
      "food truck rush tips",
      "kitchen throughput playbook",
    ],
    featureHighlights: [
      "Rush Mode auto-pauses QR promos, pings handhelds, and re-allocates expo stations during spikes.",
      "After-action reports stack orders/min, par breaches, staffing alerts, and locker pressure in one timeline.",
    ],
  },
  {
    id: "retail-inventory-fusion",
    slug: "retail-inventory-fusion",
    title: "Retail + POS Inventory: One Signal for Merch, Drinks, and Prep",
    category: "Retail & Merch",
    audience: "Hybrid restaurants, coffee chains, stadium merch teams",
    readingTime: "7 min read",
    summary:
      "Tie together packaged goods, pours, and prep with Braise Retail Inventory. Learn how combined ledgers, smart alerts, and restock automations keep both merch booths and kitchens aligned.",
    primaryKeyword: "restaurant inventory system",
    secondaryKeywords: [
      "retail pos integration",
      "multi-channel inventory restaurant",
      "real-time stock alerts",
      "restaurant merch management",
    ],
    featureHighlights: [
      "Braise Retail Inventory unifies recipe depletion, barcode counts, and handheld sales into one par board.",
      "Restock alerts can ping Slack/Teams or trigger commissary manifests automatically.",
    ],
  },
  {
    id: "payroll-bridge-phase1",
    slug: "payroll-bridge-phase1",
    title: "Payroll Bridge Phase 1: Accurate Exports Without Re-Platforming",
    category: "Payroll",
    audience: "Operators ready for labor exports but not ready to switch payroll providers",
    readingTime: "9 min read",
    summary:
      "How Payroll Bridge stages pay periods, locks hours, exports CSVs/GLs, and guards SSN access with MFA + audit logs. Learn what “estimated” means today and how entitlements gate advanced delivery.",
    primaryKeyword: "restaurant payroll exports",
    secondaryKeywords: [
      "payroll bridge braise",
      "restaurant labor reporting",
      "pay period staging tool",
      "mfa payroll security",
    ],
    featureHighlights: [
      "Stage + lock payroll periods from the same dashboard that tracks tips and timekeeping exceptions.",
      "Exports include audit trails, delivery entitlements, and estimated labels so finance trusts every file.",
    ],
  },
  {
    id: "food-hall-orchestration",
    slug: "food-hall-orchestration",
    title: "Food Hall Orchestration: Locker, QR, and Vendor Playbooks",
    category: "Enterprise",
    audience: "Food hall operators, market developers, shared-kitchen hubs",
    readingTime: "8 min read",
    summary:
      "Coordinate dozens of vendors with Braise’s shared rails: locker routing, QR menus per stall, tenant-level reporting, and centralized comms. Includes best practices from real food halls.",
    primaryKeyword: "food hall pos system",
    secondaryKeywords: [
      "multi-vendor pos",
      "locker management food hall",
      "shared kitchen technology",
      "food hall analytics",
    ],
    featureHighlights: [
      "Tenant switcher and shared automations let you pause a single vendor promo without touching others.",
      "Locker + runner dashboards reroute pick-ups when certain bays get slammed or go offline.",
    ],
  },
  {
    id: "hardware-plus-rollout",
    slug: "hardware-plus-rollout",
    title: "Hardware Plus Deployment: Own Your Devices Without Downtime",
    category: "Hardware",
    audience: "Growing restaurants, cafés, and food trucks upgrading hardware",
    readingTime: "6 min read",
    summary:
      "What comes with Braise Hardware Plus and how to roll it out in under two weeks. Covers kit selection, reader swaps, white-glove onboarding, and keeping handhelds synced to automations.",
    primaryKeyword: "restaurant hardware bundle",
    secondaryKeywords: [
      "pos hardware rollout",
      "handheld pos setup",
      "hardware plus braise",
      "food truck hardware kit",
    ],
    featureHighlights: [
      "Every Hardware Plus plan ships a pre-provisioned device, synced to your menus and automations on arrival.",
      "Swap service keeps backup devices ready so kiosks and handhelds never go dark mid-shift.",
    ],
  },
]

type ApiBlogPost = {
  id: string
  slug: string
  title: string
  summary: string
  heroImageUrl: string | null
  body: string | null
  status: string
  seoPrimaryKeyword: string | null
  seoSecondaryKeywords: string[]
  targetAudiences: string[]
  featureHighlights: string[]
  publishedAt: number | null
}

type ApiResponse = {
  ok: true
  posts: ApiBlogPost[]
}

const highlightFallbacks: Record<string, string[]> = {
  "local-restaurant-seo-playbook": [
    "Use Braise table ordering and guest messaging to collect more 5‑star reviews.",
    "Sync menu changes once in Braise so your online menu always matches what guests see.",
  ],
  "food-truck-pos-guide": [
    "Run Food Truck Mode in Braise for rush-proof handheld and QR ordering.",
    "Use Rush Mode and Autopilot to throttle promos when lines spike at events.",
  ],
  "qr-ordering-table-turns": [
    "Launch QR ordering and pay-at-table with Braise without rewriting your menu.",
    "Connect QR orders to your existing kitchen display and expo flows.",
  ],
  "restaurant-inventory-checklist": [
    "Use Braise inventory and retail tools to keep stock, sales, and recipes in one place.",
    "Get real-time alerts when key items are at risk of running out mid-service.",
  ],
}

const defaultHighlights = (audienceLabel: string): string[] => [
  `Operators like ${audienceLabel || "leading restaurants"} deploy Braise automations to stay in sync across QR, counter, handheld, and delivery rails.`,
  "Tap Rush Mode, Food Truck Mode, and Autopilot nudges to keep promotions, prep pacing, and crew communication on rails without babysitting screens.",
]

const categoryStyles: Record<
  string,
  { gradient: string; icon: LucideIcon; badge: string }
> = {
  "Local SEO": {
    gradient: "from-emerald-500 via-emerald-700 to-slate-900",
    icon: MapPin,
    badge: "SEO Systems",
  },
  "Food Trucks": {
    gradient: "from-orange-400 via-amber-500 to-slate-900",
    icon: Truck,
    badge: "Mobile Ops",
  },
  "Guest Experience": {
    gradient: "from-indigo-400 via-purple-500 to-slate-900",
    icon: UtensilsCrossed,
    badge: "Service",
  },
  Operations: {
    gradient: "from-cyan-400 via-blue-600 to-slate-900",
    icon: Bolt,
    badge: "Ops",
  },
  Automation: {
    gradient: "from-rose-400 via-fuchsia-500 to-slate-900",
    icon: Sparkles,
    badge: "AI Copilot",
  },
  Growth: {
    gradient: "from-blue-400 via-sky-500 to-slate-900",
    icon: Rocket,
    badge: "Growth",
  },
  "Retail & Merch": {
    gradient: "from-amber-400 via-orange-500 to-slate-900",
    icon: Layers,
    badge: "Retail",
  },
  Payroll: {
    gradient: "from-zinc-400 via-slate-600 to-slate-900",
    icon: Compass,
    badge: "Payroll",
  },
  Enterprise: {
    gradient: "from-lime-400 via-green-600 to-slate-900",
    icon: MapPin,
    badge: "Enterprise",
  },
  Hardware: {
    gradient: "from-slate-500 via-slate-700 to-slate-900",
    icon: Layers,
    badge: "Hardware",
  },
}

const featuredMetrics = [
  { label: "AI automations live", value: "120+", detail: "Across QR, handheld, kiosk, and expo rails." },
  { label: "Food trucks orchestrated", value: "18", detail: "Fleet operators using Rush Mode + Autopilot." },
  { label: "Retail + POS ledgers", value: "1 signal", detail: "Recipes, packaged goods, and merch stock." },
  { label: "Payroll exports staged", value: "500+", detail: "Phase 1 tenants leveraging Payroll Bridge." },
]

const estimateReadingTime = (body: string | null): string => {
  if (!body) {
    return "5 min read"
  }
  const words = body.trim().split(/\s+/).length
  const minutes = Math.max(3, Math.ceil(words / 200))
  return `${minutes} min read`
}

const BlogIndexPage = (): ReactElement => {
  const [posts, setPosts] = useState<BlogPost[]>(fallbackPosts)
  const [isFallback, setIsFallback] = useState(true)
  const pageTitle = "Restaurant POS Blog & Playbooks | Braise"
  const pageDescription =
    "SEO-ready restaurant POS playbooks for food trucks, cafés, and small restaurants. Learn local SEO, QR ordering, rush-mode operations, and AI automations from Braise."

  useEffect(() => {
    const controller = new AbortController()
    const fetchPosts = async () => {
      try {
        const response = await fetch("/api/blog/posts", { signal: controller.signal })
        if (!response.ok) {
          throw new Error(`Request failed (${response.status})`)
        }
        const data = (await response.json()) as ApiResponse
        const mapped = data.posts.map<BlogPost>((post) => {
          const audienceLabel =
            post.targetAudiences.length > 0 ? post.targetAudiences.join(", ") : "Hospitality teams"
          const highlights =
            post.featureHighlights.length > 0
              ? post.featureHighlights
              : highlightFallbacks[post.slug] ?? defaultHighlights(audienceLabel)
          return {
            id: post.id,
            slug: post.slug,
            title: post.title,
            category: post.targetAudiences[0] ?? "Playbook",
            audience: audienceLabel,
            readingTime: estimateReadingTime(post.body),
            summary: post.summary,
            primaryKeyword: post.seoPrimaryKeyword ?? "restaurant technology",
            secondaryKeywords: post.seoSecondaryKeywords,
            featureHighlights: highlights,
            heroImageUrl: post.heroImageUrl ?? undefined,
          }
        })
        if (mapped.length > 0) {
          setPosts(mapped)
          setIsFallback(false)
        }
      } catch (err) {
        if (controller.signal.aborted) {
          return
        }
        console.error("[blog] failed to fetch posts", err)
        setIsFallback(true)
      } finally {
        // no-op; fallback content already visible
      }
    }

    void fetchPosts()
    return () => controller.abort()
  }, [])

  const visiblePosts = useMemo(() => posts, [posts])

  const handleViewOutline = useCallback((slug: string) => {
    if (typeof window === "undefined") {
      return
    }
    const target = document.getElementById(slug)
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "center" })
    }
  }, [])

  return (
    <>
      <Seo
        title={pageTitle}
        description={pageDescription}
        canonicalUrl="https://braisepos.com/blog"
        noindex={isFallback}
      />
      <div className="min-h-screen bg-inverse text-inverse-foreground selection:bg-emerald-100 selection:text-emerald-900">
        <HeroHeader />
        <main className="relative">
        <section className="relative overflow-hidden border-b border-border/60 bg-gradient-to-b from-background via-primary/5 to-background px-6 py-20">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_140%_at_50%_0%,rgba(16,185,129,0.18),transparent_55%)]"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-background via-transparent to-transparent"
          />
          <div className="relative mx-auto flex max-w-6xl flex-col items-center gap-8 text-center">
            <AnimatedGroup
              variants={{
                container: {
                  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
                },
                item: {
                  hidden: { opacity: 0, filter: "blur(14px)", y: 24 },
                  visible: {
                    opacity: 1,
                    filter: "blur(0px)",
                    y: 0,
                    transition: { type: "spring", bounce: 0.25, duration: 1 },
                  },
                },
              }}
              className="flex flex-col items-center gap-5"
            >
              <Badge className="rounded-full bg-primary/10 px-5 py-2 text-xs font-semibold uppercase tracking-[0.32em] text-primary">
                Operator Playbook
              </Badge>
              <h1 className="text-balance text-4xl font-semibold leading-tight sm:text-5xl lg:text-6xl">
                Restaurant & Food Truck Growth Hub
              </h1>
              <p className="max-w-2xl text-lg text-muted-foreground">
                A living library of SEO‑friendly playbooks for local restaurants, cafés, food halls,
                and food trucks. Learn how to rank on Google, keep lines moving, and grow loyal
                regulars with modern POS, QR ordering, and inventory tools.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3 text-xs font-medium text-muted-foreground">
                <span className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card px-3 py-1.5">
                  <MapPin className="size-3 text-primary" aria-hidden />
                  <span>Local restaurant SEO</span>
                </span>
                <span className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card px-3 py-1.5">
                  <Truck className="size-3 text-primary" aria-hidden />
                  <span>Food truck POS & marketing</span>
                </span>
                <span className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card px-3 py-1.5">
                  <UtensilsCrossed className="size-3 text-primary" aria-hidden />
                  <span>Small restaurant operations</span>
                </span>
                <span className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card px-3 py-1.5">
                  <TrendingUp className="size-3 text-primary" aria-hidden />
                  <span>Growth playbooks & automation</span>
                </span>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <Button asChild size="lg" className="gap-2">
                  <Link to="#posts">
                    <BookOpen className="size-4" aria-hidden />
                    Browse playbooks
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="gap-2 border-primary/30 bg-background/80 text-primary hover:bg-primary/5"
                >
                  <Link to="/auth/signup">
                    <Rocket className="size-4" aria-hidden />
                    Talk to restaurant rollout
                  </Link>
                </Button>
              </div>
            </AnimatedGroup>
          </div>
        </section>

        <section className="px-6 py-12">
          <div className="mx-auto grid max-w-6xl gap-6 md:grid-cols-4">
            {featuredMetrics.map((metric) => (
              <Card key={metric.label} className="bg-card/95 shadow-light">
                <CardHeader className="space-y-1">
                  <CardTitle className="text-3xl font-semibold">{metric.value}</CardTitle>
                  <CardDescription className="text-xs uppercase tracking-[0.28em] text-primary">
                    {metric.label}
                  </CardDescription>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground">{metric.detail}</CardContent>
              </Card>
            ))}
          </div>
        </section>

        <section id="posts" className="px-6 pb-20">
          <div className="mx-auto flex max-w-6xl flex-col gap-10">
            <header className="flex flex-col gap-4 text-left sm:flex-row sm:items-end sm:justify-between">
              <div className="max-w-2xl space-y-2">
                <p className="text-xs font-semibold uppercase tracking-[0.32em] text-primary">
                  Blog & Playbooks
                </p>
                <h2 className="text-2xl font-semibold sm:text-3xl">
                  SEO‑ready content for local restaurants and food trucks
                </h2>
                <p className="text-sm text-muted-foreground">
                  Each playbook mirrors real operator workflows-Rush Mode, AI Staff Copilot, Retail Inventory, Payroll Bridge-
                  so teams see how Braise keeps the line calm and the ledger honest.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5">
                  <Sparkles className="size-3 text-primary" aria-hidden />
                  <span>Optimized for restaurant & food truck keywords</span>
                </span>
              </div>
            </header>
            {isFallback ? (
              <p className="text-sm text-muted-foreground">
                Featuring curated operator playbooks while we finish publishing full articles from the CMS.
              </p>
            ) : null}

            <div className="grid gap-6 md:grid-cols-2">
              {visiblePosts.map((post) => (
                <article
                  key={post.id}
                  id={post.slug}
                  className="group"
                  aria-label={post.title}
                >
                  <div className="group flex h-full flex-col overflow-hidden rounded-3xl border border-border/60 bg-card/95 shadow-xl ring-1 ring-black/5 transition hover:-translate-y-1 hover:border-primary/60">
                    <div className="relative h-48 overflow-hidden">
                      {post.heroImageUrl ? (
                        <img
                          src={post.heroImageUrl}
                          alt={`${post.title} hero`}
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div
                          className={cn(
                            "absolute inset-0 bg-gradient-to-br",
                            categoryStyles[post.category]?.gradient ?? "from-slate-700 via-slate-900 to-black",
                          )}
                        />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                      <div className="absolute inset-x-0 bottom-0 flex flex-col gap-2 p-6 text-white">
                        <div className="flex flex-wrap items-center gap-2 text-xs">
                          <Badge variant="secondary" className="bg-white/15 text-white">
                            {post.category}
                          </Badge>
                          <span className="opacity-80">{post.audience}</span>
                          <span aria-hidden>•</span>
                          <span className="opacity-80">{post.readingTime}</span>
                        </div>
                        <h3 className="text-lg font-semibold leading-snug">{post.title}</h3>
                      </div>
                      {(() => {
                        const accent = categoryStyles[post.category]
                        const Icon = accent?.icon ?? Sparkles
                        return (
                          <div className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/20 px-3 py-1 text-xs font-semibold text-white backdrop-blur">
                            <Icon className="h-3.5 w-3.5" aria-hidden />
                            <span>{accent?.badge ?? "Playbook"}</span>
                          </div>
                        )
                      })()}
                    </div>
                    <div className="flex flex-1 flex-col gap-4 p-6 text-sm text-muted-foreground">
                      <p className="text-base text-foreground">{post.summary}</p>
                      <div className="grid gap-2 text-xs">
                        <p className="font-semibold uppercase tracking-[0.3em] text-primary">Highlights</p>
                        <ul className="space-y-1">
                          {post.featureHighlights.slice(0, 2).map((highlight) => (
                            <li key={highlight} className="flex gap-2">
                              <span className="mt-1 flex size-2 rounded-full bg-primary/60" aria-hidden />
                              <span>{highlight}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <p className="sr-only">
                        Target keywords: {post.primaryKeyword}. Secondary keywords:{" "}
                        {post.secondaryKeywords.join(", ")}.
                      </p>
                    </div>
                    <div className="flex flex-col gap-3 border-t border-border/60 bg-gradient-to-r from-background via-background to-background px-6 py-4 text-xs text-muted-foreground">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-primary">
                          Braise field-tested
                        </span>
                        <span className="hidden sm:inline">Direct pull from live Braise deployments.</span>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewOutline(post.slug)}
                          className="gap-2 border-primary/40 bg-background/80 text-xs text-primary hover:bg-primary/10 hover:text-primary"
                        >
                          View outline
                          <span aria-hidden>↗</span>
                        </Button>
                        <Button
                          size="sm"
                          asChild
                          className="bg-primary text-primary-foreground hover:bg-primary/90"
                        >
                          <Link to="/talk-to-braise">
                            Talk to Braise
                            <ChevronRight className="ml-1 h-3 w-3" aria-hidden />
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <footer className="border-t border-border/60 pt-8 text-sm text-muted-foreground">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="space-y-1">
                  <h3 className="text-base font-semibold text-foreground">Want a specific playbook?</h3>
                  <p>Tell us which restaurant SEO, food truck, or ops guide you want next.</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <Button asChild size="sm" className="gap-2">
                    <Link to="/talk-to-braise">
                      Request a topic
                      <Sparkles className="size-3" aria-hidden />
                    </Link>
                  </Button>
                  <Button asChild size="sm" variant="outline" className="border-primary/30 text-primary hover:bg-primary/10">
                    <Link to="/pricing">See pricing</Link>
                  </Button>
                </div>
              </div>
            </footer>
          </div>
        </section>
      </main>
      </div>
    </>
  )
}

export default BlogIndexPage
